import React, { useContext, useState, useEffect } from "react";
import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthContext } from "../../../context/AuthContext";
import Navbar from "../../ui/Navbar";
import Loader from "../../ui/Loader";
import ManageUsers from "./Users/ManageUsers";
import ManageInstitute from "./ManageInstitute";
import { fetchWithAuth } from "../../../utils/api";
import ManageInstituteSetup from "./Institute Setup/ManageInstituteSetup";
import ManageAcademics from "./Academics/ManageAcademics";
import AdminDashboard from "./AdminDashboard";
import AdminReportCard from "./AdminReportCard";
import HelpWidget from "../../ui/HelpWidget";

import AdminCoPoDashboard from "./AdminCoPoDashboard";

export default function AdminHome() {
  document.title = "GradeMe AI - AI Assistant for Teachers";
  const { isAuthenticated, user, primaryRole } = useContext(AuthContext);
  const location = useLocation();

  const [instituteName, setInstituteName] = useState("");
  const [isCOPOEnabled, setIsCOPOEnabled] = useState(false);
  const [isReportCardEnabled, setIsReportCardEnabled] = useState(false);
  const [loadingDetails, setLoadingDetails] = useState(true);

  const [isAuthorized, setIsAuthorized] = useState(
    isAuthenticated && primaryRole === "admin",
  );

  useEffect(() => {
    setIsAuthorized(isAuthenticated && primaryRole === "admin");
  }, [isAuthenticated, primaryRole]);

  useEffect(() => {
    const fetchInstituteDetails = async () => {
      const serverUrl = process.env.REACT_APP_SERVER_URL;
      try {
        const response = await fetchWithAuth(
          `${serverUrl}/api/institute/get/${user.instituteId}`,
          {
            credentials: "include",
          },
        );
        if (!response) return;
        if (!response.ok) {
          throw new Error("Failed to fetch institute details");
        }
        const data = await response.json();
        setInstituteName(data.name);
        setIsCOPOEnabled(data.isCOPOEnabled);
        setIsReportCardEnabled(data.isReportCardEnabled !== false);
      } finally {
        setLoadingDetails(false);
      }
    };

    if (isAuthorized && user?.instituteId) {
      fetchInstituteDetails();
    }
  }, [user?.instituteId, isAuthorized]);

  if (!isAuthorized) {
    return <Navigate to="/login" replace />;
  }

  const pathParts = location.pathname.split("/");
  const currentPath = pathParts[pathParts.length - 1];
  let currentPage = "Dashboard";

  switch (currentPath) {
    case "":
      currentPage = "Dashboard";
      break;
    case "academics":
      currentPage = "Academics";
      break;
    case "institute-setup":
      currentPage = "InstituteSetup";
      break;
    case "users":
      currentPage = "Users";
      break;
    case "manage-institute":
      currentPage = "Manage";
      break;

    case "copo-dashboard":
      currentPage = "CO-PO Dashboard";
      break;
    case "report-card":
      currentPage = "Report Card";
      break;
    default:
      currentPage = "Dashboard";
  }

  // Add unique id to each navLink
  const navLinks = [
    isCOPOEnabled && {
      id: "institute-setup",
      text: "Institute Setup",
      to: "/admin/institute-setup",
      icon: "fa-solid fa-building",
    },
    {
      id: "academics",
      text: "Academics",
      to: "/admin/academics",
      icon: "fa-solid fa-book",
    },
    {
      id: "users",
      text: "Users",
      to: "/admin/users",
      icon: "fa-solid fa-users",
    },
    isReportCardEnabled && {
      id: "report-card",
      text: "Report Card",
      to: "/admin/report-card",
      icon: "fa-solid fa-file-lines",
    },
    {
      id: "manage-institute",
      text: "Manage",
      to: "/admin/manage-institute",
      icon: "fa-solid fa-user-gear",
    },

    {
      id: "dashboard",
      text: "Dashboard",
      to: "/admin/dashboard",
      icon: "fa-solid fa-chart-line",
    },
    isCOPOEnabled && {
      id: "copo-dashboard",
      text: "CO-PO Dashboard",
      to: "/admin/copo-dashboard",
      icon: "fa-solid fa-graduation-cap",
    },
  ];

  // Add unique id to each breadcrumb
  let breadcrumbs = [
    { id: "home", name: "Home", path: "/admin" },
    {
      id: "dashboard",
      name: "Dashboard",
      path: "/admin/dashboard",
      isLast: currentPage === "Dashboard",
    },
  ];

  if (isAuthorized && user && instituteName) {
    switch (currentPage) {
      case "Institute Setup":
        breadcrumbs = [
          { id: "home", name: "Home", path: "/admin" },
          {
            id: "institute-setup",
            name: "Institute Setup",
            path: "/admin/institute-setup",
            isLast: true,
          },
        ];
        break;
      case "Academics":
        breadcrumbs = [
          { id: "home", name: "Home", path: "/admin" },
          {
            id: "academics",
            name: "Academics",
            path: "/admin/academics",
            isLast: true,
          },
        ];
        break;
      case "Users":
        breadcrumbs = [
          { id: "home", name: "Home", path: "/admin" },
          { id: "users", name: "Users", path: "/admin/users", isLast: true },
        ];
        break;
      case "Manage":
        breadcrumbs = [
          { id: "home", name: "Home", path: "/admin" },
          {
            id: "manage-institute",
            name: "Manage Institute",
            path: "/admin/manage-institute",
            isLast: true,
          },
        ];
        break;

      case "CO-PO Dashboard":
        breadcrumbs = [
          { id: "home", name: "Home", path: "/admin" },
          {
            id: "copo-dashboard",
            name: "CO-PO Dashboard",
            path: "/admin/copo-dashboard",
            isLast: true,
          },
        ];
        break;
      case "Report Card":
        breadcrumbs = [
          { id: "home", name: "Home", path: "/admin" },
          {
            id: "report-card",
            name: "Report Card",
            path: "/admin/report-card",
            isLast: true,
          },
        ];
        break;
      default:
        breadcrumbs = [
          { id: "home", name: "Home", path: "/admin" },
          {
            id: "dashboard",
            name: "Dashboard",
            path: "/admin/dashboard",
            isLast: true,
          },
        ];
    }
  }

  return (
    <>
      <Navbar links={navLinks.filter(Boolean)} breadcrumbs={breadcrumbs} />
      <div className="admin-content">
        {!loadingDetails ? (
          <Routes>
            <Route 
              index 
              element={
                <Navigate 
                  to={isCOPOEnabled ? "institute-setup" : "academics"} 
                  replace 
                />
              } 
            />
            <Route path="dashboard" element={<AdminDashboard />} />
            {isCOPOEnabled && (
              <Route
                path="institute-setup"
                element={<ManageInstituteSetup />}
              />
            )}
            <Route path="academics" element={<ManageAcademics />} />
            <Route path="users" element={<ManageUsers />} />
            <Route path="manage-institute" element={<ManageInstitute />} />

            {isCOPOEnabled && (
              <Route path="copo-dashboard" element={<AdminCoPoDashboard />} />
            )}
            {isReportCardEnabled && (
              <Route path="report-card" element={<AdminReportCard />} />
            )}
            <Route path="*" element={<Navigate to="/admin" replace />} />
          </Routes>
        ) : (
          <div className="reportcard-loading">
            <Loader style={{ left: 0 }} />
          </div>
        )}
      </div>
      <HelpWidget role="admin" />
    </>
  );
}
